import type {Message} from '@pinecast/i18n';
import {defineMessages} from '@pinecast/i18n';

export const names = defineMessages({
  aud: {
    id: 'db-tip-jar-connect.currencies.aud',
    description: 'Name of currency "aud"',
    defaultMessage: 'AUD - Australian Dollar',
  },
  bgn: {
    id: 'db-tip-jar-connect.currencies.bgn',
    description: 'Name of currency "bgn"',
    defaultMessage: 'BGN - Bulgarian Lev',
  },
  brl: {
    id: 'db-tip-jar-connect.currencies.brl',
    description: 'Name of currency "brl"',
    defaultMessage: 'BRL - Brazilian Real',
  },
  cad: {
    id: 'db-tip-jar-connect.currencies.cad',
    description: 'Name of currency "cad"',
    defaultMessage: 'CAD - Canadian Dollar',
  },
  chf: {
    id: 'db-tip-jar-connect.currencies.chf',
    description: 'Name of currency "chf"',
    defaultMessage: 'CHF - Swiss Franc',
  },
  czk: {
    id: 'db-tip-jar-connect.currencies.czk',
    description: 'Name of currency "czk"',
    defaultMessage: 'CZK - Czech Koruna',
  },
  dkk: {
    id: 'db-tip-jar-connect.currencies.dkk',
    description: 'Name of currency "dkk"',
    defaultMessage: 'DKK - Danish Krone',
  },
  eur: {
    id: 'db-tip-jar-connect.currencies.eur',
    description: 'Name of currency "eur"',
    defaultMessage: 'EUR - Euro',
  },
  gbp: {
    id: 'db-tip-jar-connect.currencies.gbp',
    description: 'Name of currency "gbp"',
    defaultMessage: 'GBP - Pound Sterling',
  },
  hkd: {
    id: 'db-tip-jar-connect.currencies.hkd',
    description: 'Name of currency "hkd"',
    defaultMessage: 'HKD - Hong Kong Dollar',
  },
  huf: {
    id: 'db-tip-jar-connect.currencies.huf',
    description: 'Name of currency "huf"',
    defaultMessage: 'HUF - Hungarian Forint',
  },
  jpy: {
    id: 'db-tip-jar-connect.currencies.jpy',
    description: 'Name of currency "jpy"',
    defaultMessage: 'JPY - Japanese Yen',
  },
  nok: {
    id: 'db-tip-jar-connect.currencies.nok',
    description: 'Name of currency "nok"',
    defaultMessage: 'NOK - Norwegian Krone',
  },
  nzd: {
    id: 'db-tip-jar-connect.currencies.nzd',
    description: 'Name of currency "nzd"',
    defaultMessage: 'NZD - New Zealand Dollar',
  },
  ron: {
    id: 'db-tip-jar-connect.currencies.ron',
    description: 'Name of currency "ron"',
    defaultMessage: 'RON - Romanian Leu',
  },
  pln: {
    id: 'db-tip-jar-connect.currencies.pln',
    description: 'Name of currency "pln"',
    defaultMessage: 'PLN - Polish Złoty',
  },
  sek: {
    id: 'db-tip-jar-connect.currencies.sek',
    description: 'Name of currency "sek"',
    defaultMessage: 'SEK - Swedish Krone',
  },
  sgd: {
    id: 'db-tip-jar-connect.currencies.sgd',
    description: 'Name of currency "sgd"',
    defaultMessage: 'SGD - Singapore Dollar',
  },
  usd: {
    id: 'db-tip-jar-connect.currencies.usd',
    description: 'Name of currency "usd"',
    defaultMessage: 'USD - US Dollar',
  },
}) as Record<string, Message>;

const europeanMap = {
  eur: [
    'AT',
    'BE',
    'CH',
    'DE',
    'DK',
    'ES',
    'FI',
    'FR',
    'GB',
    'IE',
    'IT',
    'LU',
    'NL',
    'NO',
    'PT',
    'SE',
  ],
  dkk: ['DK'],
  gbp: ['GB'],
  nok: ['NO'],
  sek: ['SE'],
  usd: ['US'],
  chf: ['CH'],
};

export const countriesToCurrencies = {
  AT: {...europeanMap},
  AU: {aud: ['AU']},
  BE: {...europeanMap},
  BG: {...europeanMap, bgn: ['BG']},
  CA: {cad: ['CA'], usd: ['CA', 'US']},
  CH: {...europeanMap},
  CY: {...europeanMap},
  CZ: {...europeanMap, czk: ['CZ']},
  DE: {...europeanMap},
  DK: {...europeanMap},
  EE: {...europeanMap},
  ES: {...europeanMap},
  FI: {...europeanMap},
  FR: {...europeanMap},
  GB: {...europeanMap},
  GR: {...europeanMap},
  HK: {hkd: ['HK']},
  HU: {...europeanMap, huf: ['HU']},
  IE: {...europeanMap},
  IT: {...europeanMap},
  JP: {jpy: ['JP']},
  LT: {...europeanMap},
  LU: {...europeanMap},
  LV: {...europeanMap},
  MT: {...europeanMap},
  NL: {...europeanMap},
  NO: {...europeanMap},
  NZ: {nzd: ['NZ']},
  PL: {...europeanMap, pln: ['PL']},
  PT: {...europeanMap},
  RO: {...europeanMap, ron: ['RO']},
  SE: {...europeanMap},
  SG: {sgd: ['SG']},
  SI: {...europeanMap},
  SK: {...europeanMap},
  US: {usd: ['US']},
};

export const defaultCurrency: {
  [country in keyof typeof countriesToCurrencies]: keyof (typeof countriesToCurrencies)[country];
} = {
  AT: 'eur',
  AU: 'aud',
  BE: 'eur',
  BG: 'eur',
  CA: 'cad',
  CH: 'chf',
  CY: 'eur',
  CZ: 'czk',
  DE: 'eur',
  DK: 'dkk',
  EE: 'eur',
  ES: 'eur',
  FI: 'eur',
  FR: 'eur',
  GB: 'eur',
  GR: 'eur',
  HK: 'hkd',
  HU: 'huf',
  IE: 'eur',
  IT: 'eur',
  JP: 'jpy',
  LT: 'eur',
  LU: 'eur',
  LV: 'eur',
  MT: 'eur',
  NL: 'eur',
  NO: 'nok',
  NZ: 'nzd',
  PL: 'pln',
  PT: 'eur',
  RO: 'ron',
  SE: 'sek',
  SG: 'sgd',
  SI: 'eur',
  SK: 'eur',
  US: 'usd',
};

// Keep this in sync with tip_jar_currencies.py
export const amounts: Record<
  string,
  [number, number, number, number, number, number, number, number]
> = {
  usd: [100, 300, 500, 1000, 2000, 5000, 10000, 15000],

  brl: [500, 1500, 2500, 5000, 10000, 25000, 50000, 75000],
  czk: [2000, 6000, 10000, 20000, 40000, 100000, 200000, 300000],
  dkk: [500, 1500, 2500, 5000, 10000, 25000, 50000, 100000],
  hkd: [500, 1500, 2500, 5000, 10000, 25000, 50000, 100000],
  huf: [30000, 90000, 150000, 300000, 600000, 1500000, 3000000, 4500000],
  jpy: [10000, 30000, 50000, 100000, 200000, 500000, 1000000, 1500000],
  nok: [1000, 3000, 5000, 10000, 20000, 50000, 100000, 150000],
  ron: [400, 1200, 2000, 4000, 8000, 20000, 40000, 60000],
  pln: [400, 1200, 2000, 4000, 8000, 20000, 40000, 60000],
  sek: [500, 1500, 2500, 5000, 10000, 25000, 50000, 75000],
};
